import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconUsers } from '@sorare/core/src/atoms/icons/system/iconUsers.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandS, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { glossary } from '@sorare/core/src/lib/glossary';
import { css } from '@sorare/css';

import { SquadEmblem } from '#src/components/squad/SquadEmblem/index.tsx';

import type { SquadSearchResult_squad } from './__generated__/index.graphql.ts';

const root = css`
  padding: var(--intermediate-unit) var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: 48px;
  background-color: var(--c-nl-500);
`;

type Props = {
  squad: SquadSearchResult_squad;
  onJoin: () => void;
};

export const SquadSearchResult = ({ squad, onJoin }: Props) => {
  return (
    <SBHorizontal className={root}>
      <Horizontal gap={1}>
        <SquadEmblem level={squad.currentStepLevel} />
        <Vertical gap={0.5}>
          <BrandS>{squad.name}</BrandS>
          <Horizontal gap={0.5} center>
            <FontAwesomeIcon
              icon={iconUsers}
              size="xs"
              color="var(--c-nd-600)"
            />
            <LabelM color="var(--c-nd-600)">
              {squad.membersCount}/{squad.maxMembersCount}
            </LabelM>
            <LabelS color="var(--c-nd-400)">
              <FormattedMessage
                defaultMessage="by {nickname}"
                values={{ nickname: squad.captain.nickname }}
              />
            </LabelS>
          </Horizontal>
        </Vertical>
      </Horizontal>
      <Button size="small" color="tertiary" onClick={onJoin}>
        <FormattedMessage {...glossary.join} />
      </Button>
    </SBHorizontal>
  );
};

SquadSearchResult.fragments = {
  squad: gql`
    fragment SquadSearchResult_squad on Squad {
      id
      name
      membersCount
      maxMembersCount
      currentStepLevel
      captain {
        slug
        nickname
      }
    }
  ` as TypedDocumentNode<SquadSearchResult_squad>,
};
